import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSubmissions } from './SubmissionsContext';
import './index.css';
import Login from './screens/Login';
import Register from './screens/Register';
import Submit from './screens/Submit';
import AdminQueue from './screens/AdminQueue';
import AdminUsers from './screens/AdminUsers';
import Feed from './screens/Feed';
import MySubmissions from './screens/MySubmissions';
import ThisMonth from './screens/ThisMonth';
import AdminDashboard from './screens/AdminDashboard';
import AdminNotifications from './screens/AdminNotifications';
import AdminLogs from './screens/AdminLogs';
import ChangePassword from './screens/ChangePassword';
import Nav from './components/Nav';
import { SubmissionsProvider } from './SubmissionsContext';

const SESSION_HOURS = 8; // auto-logout after 8 hours of inactivity

// ── iOS install prompt ─────────────────────────────────────────────────────────
// Safari doesn't offer an automatic install prompt, so we show our own banner.
// Detects: iOS Safari + not already running as standalone + not dismissed before.

function useIosInstallBanner() {
  const isIos        = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const dismissed    = sessionStorage.getItem('pwa_banner_dismissed');
  return isIos && !isStandalone && !dismissed;
}

function IosInstallBanner() {
  const [visible, setVisible] = React.useState(useIosInstallBanner);
  if (!visible) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: '#fff', borderTop: '1px solid #e8e8ee',
      padding: '14px 16px 20px',
      boxShadow: '0 -4px 20px rgba(219,50,103,0.12)',
    }}>
      <button
        onClick={() => { sessionStorage.setItem('pwa_banner_dismissed', '1'); setVisible(false); }}
        style={{
          position: 'absolute', top: '10px', right: '14px',
          background: 'none', border: 'none', fontSize: '18px', color: '#bbb', cursor: 'pointer', padding: 0,
        }}
      >✕</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
        <img src="/logo192.png" alt="" style={{ width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#222' }}>Install Care Moments</div>
          <div style={{ fontSize: '12px', color: '#888' }}>Add to your home screen for the best experience</div>
        </div>
      </div>
      <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.7' }}>
        Tap <strong>Share</strong> <span style={{ fontSize: '16px' }}>⎙</span> at the bottom of Safari,
        then tap <strong>"Add to Home Screen"</strong>
      </div>
    </div>
  );
}

function loadSession() {
  try {
    const raw = sessionStorage.getItem('cm_session');
    if (!raw) return null;
    const { user, expiresAt } = JSON.parse(raw);
    if (Date.now() > expiresAt) {
      sessionStorage.removeItem('cm_session');
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

function saveSession(user) {
  sessionStorage.setItem('cm_session', JSON.stringify({
    user,
    expiresAt: Date.now() + SESSION_HOURS * 60 * 60 * 1000,
  }));
}

function RequireAdmin({ user, children }) {
  if (user.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function AppRoutes({ user, onLogout }) {
  const { loading, error } = useSubmissions();

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '14px' }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '50%',
        border: '3px solid #fce8ef', borderTop: '3px solid #DB3267',
        animation: 'spin 0.8s linear infinite',
      }} />
      <span style={{ fontSize: '13px', color: '#aaa' }}>Loading Care Moments...</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
      <div style={{ fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>Cannot connect to server</div>
      <div style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>{error}</div>
      <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', background: '#DB3267', color: '#fff', borderRadius: '8px', fontWeight: '700' }}>
        Retry
      </button>
    </div>
  );

  return (
    <>
      <Nav user={user} onLogout={onLogout} />
      <Routes>
        <Route path="/" element={<Navigate to={user.role === 'admin' ? '/dashboard' : '/feed'} />} />
        <Route path="/feed" element={<Feed user={user} />} />
        <Route path="/password" element={<ChangePassword user={user} />} />
        <Route path="/submit" element={<Submit user={user} />} />
        <Route path="/mine" element={<MySubmissions user={user} />} />
        <Route path="/thismonth" element={<ThisMonth user={user} />} />
        <Route path="/admin" element={<RequireAdmin user={user}><AdminQueue user={user} /></RequireAdmin>} />
        <Route path="/dashboard" element={<RequireAdmin user={user}><AdminDashboard /></RequireAdmin>} />
        <Route path="/users" element={<RequireAdmin user={user}><AdminUsers /></RequireAdmin>} />
        <Route path="/notifications" element={<RequireAdmin user={user}><AdminNotifications /></RequireAdmin>} />
        <Route path="/logs" element={<RequireAdmin user={user}><AdminLogs /></RequireAdmin>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

export default function App() {
  const [user, setUser] = useState(() => loadSession());
  const [showRegister, setShowRegister] = useState(false);

  // Reset expiry on any user activity
  const resetTimer = useCallback(() => {
    if (user) saveSession(user);
  }, [user]);

  useEffect(() => {
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, resetTimer));
  }, [resetTimer]);

  // Check every minute if session has expired
  useEffect(() => {
    const interval = setInterval(() => {
      if (user && !loadSession()) handleLogout();
    }, 60 * 1000);
    return () => clearInterval(interval);
  });

  function handleLogin(u) {
    saveSession(u);
    setUser(u);
  }

  function handleLogout() {
    sessionStorage.removeItem('cm_session');
    setUser(null);
  }

  if (!user) {
    if (showRegister) return <Register />;
    return (
      <>
        <Login onLogin={handleLogin} onRegister={() => setShowRegister(true)} />
        <IosInstallBanner />
      </>
    );
  }

  return (
    <SubmissionsProvider userId={user.id}>
      <BrowserRouter>
        <AppRoutes user={user} onLogout={handleLogout} />
        <IosInstallBanner />
      </BrowserRouter>
    </SubmissionsProvider>
  );
}
